import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import type { UsageData } from '@/features/customers/types/customerDetail.types'

interface RevenueChartProps {
  data: UsageData[]
  title?: string
  height?: number
  showMargin?: boolean
}

export function RevenueChart({
  data,
  title = 'Revenue & Cost Trends',
  height = 300,
  showMargin = true,
}: RevenueChartProps) {
  const chartData = data.map(item => ({
    ...item,
    margin: item.revenue - item.cost,
  }))

  return (
    <div className='space-y-4'>
      <h3 className='text-lg font-semibold'>{title}</h3>
      <ResponsiveContainer width='100%' height={height}>
        <AreaChart data={chartData}>
          <CartesianGrid strokeDasharray='3 3' />
          <XAxis
            dataKey='date'
            tick={{ fontSize: 12 }}
            tickFormatter={value => {
              const date = new Date(value)
              return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
              })
            }}
          />
          <YAxis tick={{ fontSize: 12 }} tickFormatter={value => `$${value}`} />
          <Tooltip
            labelFormatter={value => {
              const date = new Date(value as string)
              return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })
            }}
            formatter={
              ((value: number, name: string) => [
                `$${value.toFixed(2)}`,
                name.charAt(0).toUpperCase() + name.slice(1),
              ]) as never
            }
          />
          <Legend />
          <Area
            type='monotone'
            dataKey='revenue'
            stackId='1'
            stroke='#10b981'
            fill='#10b981'
            fillOpacity={0.6}
            name='Revenue'
          />
          <Area
            type='monotone'
            dataKey='cost'
            stackId='2'
            stroke='#ef4444'
            fill='#ef4444'
            fillOpacity={0.6}
            name='Cost'
          />
          {showMargin && (
            <Line
              type='monotone'
              dataKey='margin'
              stroke='#8b5cf6'
              strokeWidth={2}
              name='Margin'
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
            />
          )}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}
